import React, { useState, useEffect, useRef, memo } from 'react';
import clsx from 'clsx';
import { isMobile } from 'react-device-detect';
import moment from 'moment';
import 'react-image-lightbox/style.css';
import Lightbox from 'react-image-lightbox';
import { decryptImageFromUrl } from 'hooks/useImageDecrypt';
import { updateTimelineContent } from 'api/timelineAPI';
import { useDispatch, useSelector } from 'react-redux';

import ActionTooltips from './Timeline/ActionTooltips';
import { setImageValue, selectImageValue } from 'store/encryptedImagesSlice';
import { RootState } from 'store';
import EncytedImage from './EncytedImage';
import EditableField from './Timeline/EditableField';

interface ITimeline {
  timeline: any;
  displayAvatar: boolean;
  navigateToVideo: (time: number) => void;
  user: any;
  userData?: any;
  aesKeyId: string;
  decryptionKeys: any;
  videoHashId: string;
  channelId?: string;
  handleEdit: (hashId: string) => void;
  handleSpeaker: () => void;
  handleCancelEdit: () => void;
  isEditing: boolean;
  videoShotId: string;
  onDelete: () => void;
  searchText?: string;
  isExternal?: boolean;
  secondaryViewSize?: number;
  isResizing?: boolean;
  index?: number;
  hoveredId?: number;
  handleHoveredId?: (hashId: string) => void;
}

const formatTime = (time: number) => {
  return time
    ? time > 3600
      ? moment.duration(time, 'seconds').format('h:mm:ss', { trim: false })
      : moment.duration(time, 'seconds').format('m:ss', { trim: false })
    : '';
};

function TimelineItem({
  timeline,
  displayAvatar,
  navigateToVideo,
  user,
  userData,
  aesKeyId,
  decryptionKeys,
  videoHashId,
  channelId,
  handleEdit,
  isEditing,
  videoShotId,
  handleSpeaker,
  handleCancelEdit,
  onDelete,
  searchText,
  isExternal,
  secondaryViewSize,
  isResizing,
  index,
  hoveredId,
  handleHoveredId
}: ITimeline) {
  const [hovered, setHovered] = useState(false);
  const [openLightbox, setOpenLightbox] = useState<boolean>(false);
  const [imageLoading, setImageLoading] = useState<boolean>(false);
  const [largeImage, setLargeImage] = useState<string | null>('');
  // const [mediumImage, setMediumImage] = useState<string | null>('');
  const [loading, setLoading] = useState(true);
  const [editedContent, setEditedContent] = useState(timeline.summary);

  const cleanTitle = (title: string) => {
    if (!title?.length) return '';
    return title.split('"').join('');
  };

  const [editedTitle, setEditedTitle] = useState(cleanTitle(timeline.title));
  const [editedHighligtedText, setEditedHighligtedText] = useState(timeline.highlighted_text);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isDisplay, setisDisplay] = useState<boolean>(true);
  // const dispatch = useDispatch();

  // const image = useSelector((state: RootState) => selectImageValue(state.encryptedImages, timeline.medium_img));

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      const textarea = textareaRef.current;
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
      textareaRef.current.focus();
      textarea.setSelectionRange(textarea.value.length, textarea.value.length);
    }
  }, [isEditing]);

  const onCancelEdit = () => {
    handleCancelEdit();
    setHovered(true);
  };

  const handleSave = async () => {
    try {
      await updateTimelineContent(videoHashId, videoShotId, timeline.hash_id, { summary: editedContent });
      handleCancelEdit();
    } catch (error) {
      handleCancelEdit();
    }
  };

  const deleteTimeline = async () => {
    onDelete();
  };

  useEffect(() => {
    if (isResizing) {
      setisDisplay(false);
    } else {
      setisDisplay(true);
    }
  }, [isResizing]);

  useEffect(() => {
    let isMounted = true;
    if (decryptionKeys?.iv && timeline.medium_img && openLightbox) {
      setImageLoading(true);
      const { key, iv } = decryptionKeys;
      if (isMounted) {
        decryptImageFromUrl(timeline.large_img, key, iv).then((decryptedUrl) => {
          setLargeImage(decryptedUrl);
          setImageLoading(false);
          setLoading(false);
        });
      }
    } else {
      if (timeline.large_img) {
        setLargeImage(timeline.large_img);
        setImageLoading(false);
        setLoading(false);
      }
    }
    return () => {
      isMounted = false;
      setLoading(true);
    };
  }, [openLightbox]);

  // useEffect(() => {
  //   const decryptImage = () => {
  //     // const { key, iv } = decryptionKeys;
  //     decryptImageFromUrl(timeline.medium_img, key, iv).then((decryptedUrl) => {
  //       if (isMounted) {
  //         setMediumImage(decryptedUrl);
  //         dispatch(setImageValue({ url: timeline.medium_img, value: decryptedUrl }));
  //         setImageLoading(false);
  //       }
  //     });
  //   };

  //   let isMounted = true;
  //   if (timeline.medium_img) {
  //     console.log('image', image);
  //     if (image) {
  //       console.log('image found>>>', image);
  //       setMediumImage(image);
  //       setImageLoading(false);
  //     } else {
  //       decryptImage();
  //     }
  //   } else {
  //     if (timeline.medium_img) {
  //       setMediumImage(timeline.medium_img);
  //       setImageLoading(false);
  //     }
  //   }
  //   return () => {
  //     isMounted = false;
  //     setMediumImage(null);
  //   };
  // }, [timeline.id]);

  const highlightText = (text: string) => {
    if (!searchText) return text; // Return original text if no search term
    // Escape special characters in searchText
    const escapedSearchText = searchText.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
    const regex = new RegExp(`(${escapedSearchText})`, 'gi'); // Create a regex for case-insensitive matching
    const parts = text?.split(regex); // Split text based on the regex

    return parts?.map((part: string, index: number) =>
      regex.test(part) ? (
        <span key={index} style={{ backgroundColor: '#F6F7C4' }}>
          {part}
        </span>
      ) : (
        part
      )
    ); // Highlight matched parts
  };

  useEffect(() => {
    return () => {
      // Reset all state variables
      setHovered(false);
      setOpenLightbox(false);
      setImageLoading(false);
      setLargeImage(null);
      // setMediumImage(null);
      setLoading(true);
      setEditedContent('');
      setEditedTitle('');

      // Reset the textarea reference
      if (textareaRef.current) {
        textareaRef.current.value = '';
      }
    };
  }, [timeline.id]);

  return (
    <div>
      {timeline.item_type === 'chapter' ? (
        <>
          {index !== 0 && (
            <div className="py-3 mr-4 ml-4 pt-0">
              <div className="border-b border-line"></div>
            </div>
          )}
          <div
            className={`px-4  ${isEditing ? 'bg-[#f2c74433] py-1' : ''}`}
            onMouseEnter={() => !isMobile && setHovered(true)}
            onMouseLeave={() => !isMobile && setHovered(false)}
            onTouchStart={() => {
              if (isMobile && handleHoveredId) {
                handleHoveredId(timeline.hash_id);
              }
            }}
          >
            <h2
              className={`mr-[5px] text-iconColor font-lato no-underline font-black text-[15px] leading-[1.4] break-words ${
                isEditing ? 'hidden' : 'visible'
              }`}
            >
              {cleanTitle(timeline.title)}
            </h2>
            {isEditing && (
              <EditableField
                value={editedTitle}
                onChange={setEditedTitle}
                onCancel={onCancelEdit}
                onSave={async () => {
                  await updateTimelineContent(videoHashId, videoShotId, timeline.hash_id, { title: editedTitle });
                  handleCancelEdit();
                }}
                ref={textareaRef}
              />
            )}
            {hovered && !isEditing && !isExternal && isDisplay && (
              <ActionTooltips
                hashId={timeline.hash_id}
                itemType={timeline.item_type}
                handleEdit={() => {
                  handleEdit(timeline.hash_id);
                }}
                handleSpeaker={handleSpeaker}
                onDelete={deleteTimeline}
              ></ActionTooltips>
            )}
          </div>
        </>
      ) : (
        <div
          className={`flex items-center pl-4 font-lato relative pr-4 transition duration-100 text-sm ${
            isEditing ? 'bg-[#f2c74433] py-1' : ''
          }`}
          onMouseEnter={() => !isMobile && setHovered(true)}
          onMouseLeave={() => !isMobile && setHovered(false)}
          onTouchStart={() => {
            if (isMobile && handleHoveredId) {
              handleHoveredId(timeline.hash_id);
            }
          }}
        >
          <div className="flex items-center w-full">
            <div className="mr-[10px] mb-auto shrink-0 text-right leading-[1.4]">
              {displayAvatar && !timeline.hide_speaker ? (
                <img
                  className="rounded-full mt-[2px] w-[36px] h-[36px]"
                  src={
                    user.avatar
                      ? user.avatar.includes('http')
                        ? user.avatar
                        : userData?.workspace?.url + user.avatar
                      : '/assets/img/unknown-avatar.png'
                  }
                  width={36}
                  height={36}
                  alt={`${user?.display_name} - Image`}
                />
              ) : (
                <div className="mt-[2px]">
                  <p
                    className={clsx('text-xs font-normal w-11 -ml-2 cursor-pointer text-[#1664A3]', {
                      visible: hovered || isMobile,
                      invisible: !hovered && !isMobile
                    })}
                    onClick={() => navigateToVideo(timeline.timestamp)}
                    onTouchEnd={() => navigateToVideo(timeline.timestamp)}
                  >
                    {formatTime(timeline.timestamp)}
                  </p>
                </div>
              )}
            </div>
            <div className="w-full grow shrink -mr-2 -ml-4 pr-2 pl-4 leading-[1.4] flex flex-wrap flex-col">
              {displayAvatar ? (
                <div>
                  <a className="mr-[5px] text-iconColor font-lato no-underline font-black text-[15px]">
                    {user.display_name || 'Unknown'}
                  </a>
                  <small
                    className="text-xs font-normal text-[#1664A3] cursor-pointer"
                    onClick={() => navigateToVideo(timeline.timestamp)}
                  >
                    {formatTime(timeline.timestamp)}
                  </small>
                </div>
              ) : (
                ''
              )}
              <>
                {timeline && timeline.item_type === 'sentence' ? (
                  <>
                    {isEditing ? (
                      <EditableField
                        value={editedContent}
                        onChange={setEditedContent}
                        onCancel={onCancelEdit}
                        onSave={handleSave}
                        ref={textareaRef}
                      />
                    ) : (
                      <>
                        {searchText ? (
                          <p>{highlightText(timeline.summary)}</p>
                        ) : (
                          <p className="text-sm font-lato font-normal text-black leading-[1.4] break-words">
                            {timeline.summary}
                          </p>
                        )}
                      </>
                    )}
                  </>
                ) : timeline.item_type === 'keyframe' ? (
                  <>
                    <div className="bg-transparent group relative">
                      <div className="text-sm font-lato font-normal text-black leading-[1.4] break-words">
                        <div className={`${isEditing ? 'py-1' : ''}`}>
                          {isEditing ? (
                            <EditableField
                              value={editedHighligtedText}
                              onChange={setEditedHighligtedText}
                              onCancel={onCancelEdit}
                              onSave={async () => {
                                await updateTimelineContent(videoHashId, videoShotId, timeline.hash_id, {
                                  highlighted_text: editedHighligtedText
                                });
                                handleCancelEdit();
                              }}
                              ref={textareaRef}
                            />
                          ) : (
                            <p className="mb-1">{highlightText(timeline.highlighted_text)}</p>
                          )}
                        </div>
                      </div>
                      {isExternal ? (
                        <div className={`bg-transparent group relative mt-1 h-[180px]`}>
                          {decryptionKeys?.key ? (
                            <>
                              <EncytedImage
                                videoHashId={videoHashId}
                                encryptedImageUrl={timeline.medium_img}
                                decryptionKeys={decryptionKeys}
                                navigateToVideo={navigateToVideo}
                                timestamp={timeline.timestamp}
                                secondaryViewSize={400}
                                isExternal={true}
                              />
                              <div
                                className="absolute text-white bg-[#515151] px-1 py-1 text-sm items-center justify-center rounded-md bottom-6 right-6 md:group-hover:flex hidden cursor-pointer"
                                onClick={() => setOpenLightbox(true)}
                              >
                                Click to enlarge
                              </div>
                            </>
                          ) : (
                            <img
                              src={timeline.medium_img}
                              alt={`thumbnail image ${timeline.hash_id}`}
                              className="h-full w-[100%] max-w-[360px] object-fit transition-opacity duration-800 ease-in-out mt-1 rounded-lg cursor-pointer"
                              onClick={() => navigateToVideo(timeline.timestamp)}
                            />
                          )}
                        </div>
                      ) : (
                        <>
                          {timeline.medium_img && (
                            <>
                              <div
                                className={`relative bg-transparent ${
                                  !isMobile && secondaryViewSize && secondaryViewSize > 400
                                    ? 'h-[210px]'
                                    : isMobile
                                    ? 'h-[210px]'
                                    : 'h-[140px]'
                                }`}
                              >
                                {decryptionKeys?.key ? (
                                  <EncytedImage
                                    key={videoHashId + timeline.medium_img}
                                    videoHashId={videoHashId}
                                    encryptedImageUrl={timeline.medium_img}
                                    decryptionKeys={decryptionKeys}
                                    timestamp={timeline.timestamp}
                                    navigateToVideo={navigateToVideo}
                                    secondaryViewSize={secondaryViewSize}
                                  />
                                ) : (
                                  <img
                                    src={timeline.medium_img}
                                    alt={`thumbnail image ${timeline.hash_id}`}
                                    className="h-full w-[100%] max-w-[360px] object-fit transition-opacity duration-800 ease-in-out mt-1 rounded-lg cursor-pointer"
                                    onClick={() => navigateToVideo(timeline.timestamp)}
                                  />
                                )}
                                <div
                                  className="absolute right-6 text-white bg-[#515151] px-1 py-1 text-sm items-center justify-center rounded-md bottom-6 flex md:hidden md:group-hover:flex cursor-pointer"
                                  onClick={() => setOpenLightbox(true)}
                                >
                                  Click to enlarge
                                </div>
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </div>
                    {openLightbox && largeImage && (
                      <Lightbox
                        mainSrc={largeImage}
                        onCloseRequest={() => setOpenLightbox(false)}
                        onImageLoad={() => setImageLoading(false)}
                        imageLoadErrorMessage={imageLoading ? '' : 'This image failed to load '}
                      />
                    )}
                  </>
                ) : (
                  ''
                )}
              </>
              {hovered && !isEditing && !isExternal && isDisplay && !isMobile && (
                <ActionTooltips
                  hashId={timeline.hash_id}
                  itemType={timeline.item_type}
                  handleEdit={() => {
                    handleEdit(timeline.hash_id);
                  }}
                  handleSpeaker={handleSpeaker}
                  onDelete={deleteTimeline}
                  hasHighligtedText={timeline.highlighted_text?.length > 0}
                ></ActionTooltips>
              )}

              {!isEditing && !isExternal && isDisplay && isMobile && (
                <div className={hoveredId === timeline.hash_id ? 'visible' : 'hidden'}>
                  <ActionTooltips
                    hashId={timeline.hash_id}
                    itemType={timeline.item_type}
                    handleEdit={() => {
                      handleEdit(timeline.hash_id);
                    }}
                    handleSpeaker={handleSpeaker}
                    onDelete={deleteTimeline}
                    hasHighligtedText={timeline.highlighted_text?.length > 0}
                  ></ActionTooltips>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default memo(TimelineItem);
